import React, { useState, useEffect, useRef, ReactElement, Fragment } from 'react';
import styled, { CSSObject } from '@emotion/styled';
import { getPatternModal } from '../../../style';
import Avatar from '../../../atoms/Avatar';
import Headline from '../../common/Headline';
import TextLink from '../../common/TextLink';
import PriceInput, { adjustStylePerDigit } from './PriceInput';
import Button from '../../../atoms/buttons/Button';
import ButtonSet from '../../blocks/ButtonSet';
import { IsMobileViewPort } from '../../../utils/helpers';
import {
  counterOffer,
  counterCancel,
  counterModalCustomAmount,
  counterModalClose,
} from '../../../actions/profileTrackAction';
import useModal from '../../../hooks/useModal';
import useOnClickTriggerTracking from '../../../hooks/useOnclickTriggerTracking';
import { PriceContainer, getInputProps, CurrencyTip, CurrencyLabelSpan } from './common';
import WYPButtonIcon from '../../../atoms/icons/WYPButtonIcon';
import useMandatoryWall from '../../../hooks/useMandatoryWall';
import { mobileOverlayStyle, mobileContentStyle } from '../../common/CommonStyle';
import ModalWithPattern from '../../common/modals/ModalWithPattern';
import OfferModalFooter from './OfferModalFooter';
import breakpoints from 'theme/material/breakpoints';
import { isMobile } from 'react-device-detect';
import borders from 'theme/borders';

const Form = styled('form')({
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const HeaderDiv = styled('div')({
  textAlign: 'center',

  [breakpoints.down(639.05)]: {
    marginTop: 10,
  },
});

const AvatarTitleDiv = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  gap: 20,
});

const TitleDiv = styled('span')(
  (): CSSObject => ({
    color: getPatternModal().titleColor,
    fontSize: 36,
    fontWeight: 800,
    lineHeight: '40px',
    letterSpacing: -0.3,
    textTransform: 'uppercase',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    [breakpoints.down(639.05)]: {
      fontSize: 30,
    },
  })
);

const Content = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  flexGrow: '1',
  justifyContent: 'space-between',
});

const colorType = (price, hasErrors) => {
  switch (price) {
    case true:
      return 'default';
    case false:
      return !hasErrors ? 'brand' : 'alert';
    default:
      return;
  }
};

const CounterRange = styled('div')({
  fontSize: 14,
  lineHeight: '19px',
  letterSpacing: '-0.3px',
  marginTop: 20,
  marginBottom: 20,
  textAlign: 'center',
  color: getPatternModal().headerColor,
});

const ErrorContainer = styled('div')({
  textAlign: 'center',
  marginTop: '16px',
});

const ErrorMessage = styled('div')({
  color: 'rgb(204, 84, 84)',
  margin: '16px auto 0',
  maxWidth: '280px',
});

const PurchaseCredits = styled('div')({
  textAlign: 'center',
  marginTop: '12px',
  textDecoration: 'underline',
});

const ButtonDiv = styled('div')({
  marginTop: isMobile ? 0 : 20,
});

const getHeadline = username => {
  return (
    <span>
      Counter <strong>{username}&lsquo;s</strong> offer by submitting a new amount below.
    </span>
  );
};

const offerMinMaxVal = (offer, type, defaultAmount) => {
  const offerVal = offer && type === 'min' ? offer.min_counter_price : offer && offer.max_counter_price;

  return offer ? offerVal : defaultAmount;
};

const validateTextInput = e => {
  const Key = e.key;
  const regex = /[0-9]/;

  if (!regex.test(Key)) {
    if (e.preventDefault) e.preventDefault();
  }
};

const renderCounterRange = (currencyInitial, currLabel, minValue, maxValue) => {
  return (
    <CounterRange>
      Enter a new amount between {currencyInitial}
      {currLabel}
      {maxValue} and {currencyInitial}
      {currLabel}
      {minValue}.
    </CounterRange>
  );
};

const setCreateOfferAction = (e, offerAction) => {
  switch (offerAction) {
    case 'Send Counter':
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      counterOffer(e);
      break;
    case 'Cancel Counter':
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      counterCancel(e);
      break;
    case 'Counter Modal Close':
      // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
      counterModalClose(e);
      break;
    default:
      return;
  }
};

const trackFocus = (ref, actionMethod) => {
  if (ref && ref.current) {
    ref.current.addEventListener('click', e => actionMethod(e), false);
  }

  return () => {
    ref.current.removeEventListener('click', () => null, false);
  };
};

interface OfferCounterOfferProps {
  isModalOpen: boolean;
  avatarSrc: any;
  offer: any;
  errors: any;
  closeModal: any;
  currentUserIsGenerous: any;
  clearErrors: any;
  username: any;
  counterOffer: any;
  hashId: any;
  buttonID: any;
  successOpen: any;
  mixpanelEventSource?: string;
  customContentStyle?: any;
}

const OfferCounterOffer = ({
  isModalOpen,
  avatarSrc,
  offer,
  errors,
  closeModal,
  currentUserIsGenerous,
  clearErrors,
  username,
  counterOffer,
  hashId,
  buttonID,
  successOpen,
  mixpanelEventSource,
  customContentStyle,
}: OfferCounterOfferProps): ReactElement => {
  const [defaultValue] = useState('');
  const [offerPrice, setOfferPrice] = useState('');
  const [minValue, setMinValue] = useState(offerMinMaxVal(offer.data, 'min', '5'));
  const [maxValue, setMaxValue] = useState(offerMinMaxVal(offer.data, 'max', '500'));
  const [hasErrors, setHasErrors] = useState(false);
  const [isOfferSending, setOfferSending] = useState(false);
  const sendOfferBtn = useRef(null);
  const sendCancelBtn = useRef(null);
  const priceInput = useRef(null);
  const [currencyInitial, setCurrencyInitial] = useState('');
  const [currencyLabel, setCurrencyLabel] = useState('');
  const { showMandatoryWall } = useMandatoryWall();
  const { resetModal, setModalStateHandler } = useModal();

  useOnClickTriggerTracking('Cancel Counter', sendCancelBtn, setCreateOfferAction);

  useOnClickTriggerTracking('Counter Modal Close', sendCancelBtn, setCreateOfferAction);

  useEffect(() => {
    setModalStateHandler({
      onCloseClick: () => {
        counterModalClose();
        resetModal();
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    clearErrors();
    trackFocus(priceInput, counterModalCustomAmount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const { data } = offer;

    setMaxValue(offerMinMaxVal(data, 'max', '500'));
    setMinValue(offerMinMaxVal(data, 'min', '5'));
    setCurrencyInitial(data.currency_original.data.initial);
    setCurrencyLabel(data.currency_original.data.label_unicode);
  }, [offer]);

  useEffect(() => {
    setOfferPrice('');
    if (priceInput.current) {
      priceInput.current.focus();
    }
  }, [priceInput]);

  const updateOfferPrice = (val, error) => {
    if (!error) {
      clearErrors();
    }
    setOfferPrice(val);
    setHasErrors(error || false);
  };

  const inputOnChange = e => {
    if (!e.target || typeof e.target.value !== 'string') {
      return;
    }

    const inputVal = Number(e.target.value);
    const minValueNum = Number(minValue);
    const maxValueNum = Number(maxValue);
    const maxLength = maxValueNum.toString().length;

    if (e.target.value.length <= maxLength) {
      const isError = inputVal < minValueNum || inputVal > maxValueNum;

      updateOfferPrice(inputVal, isError);
    } else {
      e.preventDefault();
    }
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = { source: mixpanelEventSource, price: offerPrice, account: hashId };
    const offerAmount = `${currencyInitial}${currLabel}${offerPrice}`;

    setOfferSending(true);
    showMandatoryWall(
      () => {
        setCreateOfferAction(e, 'Send Counter');

        counterOffer(formData)
          .then(() => {
            closeModal();
            setOfferSending(false);
            successOpen(true, offerAmount, true);
          })
          .catch(() => {
            setOfferSending(false);
            setHasErrors(true);
          });
      },
      () => setOfferSending(false),
      'Counter Offer'
    );
  };

  // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
  const currLabel = String.fromCharCode(currencyLabel);

  return (
    <ModalWithPattern
      isModalOpen={isModalOpen}
      dataTestID="counter-offer-modal"
      closeBtnClassName="counter-offer-modal-close"
      closeBtnDataTestId="counter-offer-modal-close"
      modalContentLabel="Counter Offer Modal"
      onCloseClick={closeModal}
      closeRequest={closeModal}
      customOverlayStyle={IsMobileViewPort ? mobileOverlayStyle : {}}
      customContentStyle={IsMobileViewPort ? { ...mobileContentStyle, ...customContentStyle } : customContentStyle}
      withoutBgPattern={false}
      hideCloseBtn={false}
      closeOnOverlayClick={false}
      enableTouchMove={true}
    >
      <Fragment>
        <Form>
          <HeaderDiv>
            <AvatarTitleDiv>
              <Avatar
                data-test-id="offer-modal-avatar"
                src={avatarSrc}
                width={'100px'}
                customStyle={{
                  backgroundColor: '#ccc',
                  border: `1px solid ${borders.light.primary}`,
                }}
              />
              <TitleDiv>Counter Offer!</TitleDiv>
            </AvatarTitleDiv>
            <Headline
              style={{
                color: getPatternModal().headerColor,
                marginTop: 20,
                fontSize: 16,
                lineHeight: '21px',
                letterSpacing: '-0.3px',
              }}
              data-test-id="profile-offer-headline"
              size="SM"
            >
              {getHeadline(username)}
            </Headline>
          </HeaderDiv>
          <Content>
            <div>
              <PriceContainer
                // @ts-expect-error To be fix in https://reflexmedia.atlassian.net/browse/WYP-16389
                hasErrors={hasErrors}
              >
                <CurrencyTip
                  offerPrice={colorType(offerPrice, hasErrors)}
                  style={{
                    paddingLeft: adjustStylePerDigit(offerPrice).paddingLeft,
                  }}
                >
                  <CurrencyLabelSpan
                    customCurrencyLabelStyle={{
                      fontSize: 60,
                      fontWeight: 400,
                      lineHeight: '50px',
                      letterSpacing: -0.3,
                      [breakpoints.down('sm')]: { fontSize: '50px', lineHeight: '50px' },
                    }}
                  >
                    {currencyInitial}
                    {currLabel}
                  </CurrencyLabelSpan>
                  <PriceInput
                    inputRef={priceInput}
                    data-test-id="offer-price-input"
                    hasErrors={hasErrors}
                    value={offerPrice}
                    onChange={inputOnChange}
                    validateTextFn={validateTextInput}
                    placeholder={defaultValue}
                    inputProps={{
                      style: {
                        ...getInputProps().style,
                        marginLeft: adjustStylePerDigit(offerPrice).marginLeft,
                        textAlign: 'left',
                        fontSize: 60,
                        fontWeight: 400,
                        lineHeight: '50px',
                        letterSpacing: -0.3,
                      },
                    }}
                    minWidth={IsMobileViewPort ? 100 : 120}
                    responsiveFontSize={`
                      @media (max-width: 1023.95px) {
                        font-size: 50px !important;
                        line-height: 50px !important;
                      }
                   `}
                  />
                </CurrencyTip>
              </PriceContainer>
              {renderCounterRange(currencyInitial, currLabel, minValue, maxValue)}
              {errors && Object.keys(errors).length ? (
                <ErrorContainer>
                  <ErrorMessage>{errors.price}</ErrorMessage>
                  {currentUserIsGenerous && Number(offerPrice) >= 200 && (
                    <PurchaseCredits>
                      <TextLink to="/packages" onClick={() => closeModal()}>
                        Purchase credits now.
                      </TextLink>
                    </PurchaseCredits>
                  )}
                </ErrorContainer>
              ) : null}
            </div>
            <ButtonDiv>
              <ButtonSet align="center">
                <Button
                  id={buttonID}
                  buttonRef={sendOfferBtn}
                  disabled={hasErrors || isOfferSending}
                  buttonType="error"
                  onClick={handleSubmit}
                  type="submit"
                  data-test-id="modal-accept-offer-btn"
                  customStyle={{
                    fontSize: 18,
                    lineHeight: '16px',
                    letterSpacing: -0.3,
                    padding: '12px 28px',
                    borderRadius: '4px !important',
                  }}
                  buttonTextCustomStyle={{ alignItems: 'center' }}
                >
                  <WYPButtonIcon style={{ marginRight: 6 }} />
                  Send Counter Offer
                </Button>
              </ButtonSet>
              <ButtonSet align="center">
                <Button
                  buttonType="chromeless"
                  buttonRef={sendCancelBtn}
                  data-test-id={`${buttonID}Cancel`}
                  disabled={isOfferSending}
                  onClick={e => {
                    e.preventDefault();
                    closeModal();
                  }}
                  customStyle={{
                    color: '#8392A7',
                    fontWeight: 400,
                    fontSize: 15,
                    lineHeight: '17px',
                    height: 'auto',
                    padding: '20px 0',
                  }}
                >
                  {' '}
                  Cancel
                </Button>
              </ButtonSet>
            </ButtonDiv>
          </Content>
        </Form>
      </Fragment>
      <OfferModalFooter />
    </ModalWithPattern>
  );
};

export default OfferCounterOffer;
